import { ComboboxPaginationState } from "@/components/ui/combobox";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useMemo, useState } from "react";

export const DEFAULT_COMBOBOX_PAGE_SIZE = 50;

export interface UseComboboxPaginationOptions {
  initialPageSize?: number;
  maxPageSize?: number;
  debounceMs?: number;
}

export interface UseComboboxPaginationResult {
  searchTerm: string;
  debouncedSearchTerm: string;
  pageSize: number;
  createPaginationState: (
    total: number | undefined,
    loadedCount: number,
    isLoading: boolean,
  ) => ComboboxPaginationState;
}

export function useComboboxPagination({
  initialPageSize = DEFAULT_COMBOBOX_PAGE_SIZE,
  maxPageSize,
  debounceMs = 300,
}: UseComboboxPaginationOptions = {}): UseComboboxPaginationResult {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs);

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setPageSize(initialPageSize);
    },
    [initialPageSize],
  );

  const handleLoadMore = useCallback(() => {
    setPageSize((prev) => Math.min(prev + initialPageSize, maxPageSize ?? Number.POSITIVE_INFINITY));
  }, [initialPageSize, maxPageSize]);

  const createPaginationState = useCallback(
    (total: number | undefined, loadedCount: number, isLoading: boolean): ComboboxPaginationState => {
      const remainingCount = Math.max(0, (total ?? 0) - loadedCount);
      const hasMore = loadedCount < (total ?? 0) && (maxPageSize === undefined || pageSize < maxPageSize);
      const isLoadingMore = isLoading && pageSize > initialPageSize;

      return {
        isLoading,
        hasMore,
        remainingCount,
        isLoadingMore,
        onSearchChange: handleSearchChange,
        onLoadMore: handleLoadMore,
      };
    },
    [handleSearchChange, handleLoadMore, pageSize, initialPageSize, maxPageSize],
  );

  return useMemo(
    () => ({
      searchTerm,
      debouncedSearchTerm,
      pageSize,
      createPaginationState,
    }),
    [searchTerm, debouncedSearchTerm, pageSize, createPaginationState],
  );
}
